import type { TextKey } from "../i18n/dictionary"
import type { KnownPermission } from "../access/permissions"

/**
 * Разделы кабинета.
 *
 * ПОРЯДОК ФИКСИРОВАН и не перестраивается ни по частоте использования, ни по правам
 * (01-основа.md §4.3 п.2): администратор находит «Клиенты» вторым пунктом мышечной памятью,
 * и меню, которое переставляет себя само, эту память ломает. Раздел без права не уезжает
 * вверх — он просто не показывается.
 *
 * Адрес есть у каждого раздела: состояние кабинета живёт в адресной строке, а не во вкладках
 * (ТЗ §4). Поэтому здесь адрес, а не идентификатор вкладки.
 */

export const CABINET_PATHS = {
  // Сводка стоит ПЕРВОЙ намеренно: firstAllowedPath ведёт с корня в первый доступный
  // раздел, и владелец после входа обязан попасть на сводку, а не в журнал. Мастер
  // без reports.view по-прежнему попадёт в журнал — порядок решает это сам.
  summary: "/summary",
  notifications: "/notifications",
  journal: "/journal",
  clients: "/clients",
  catalog: "/catalog",
  staff: "/staff",
  branches: "/branches",
  reports: "/reports",
  channels: "/channels",
  // Разделы заведены ЗАРАНЕЕ и пустыми: меню, которое перестраивается от обновления
  // к обновлению, человек перестаёт читать глазами и ищет пункт заново каждый раз.
  campaigns: "/campaigns",
  audit: "/audit",
  billing: "/billing",
  developers: "/developers",
  settings: "/settings",
  support: "/support",
} as const

export type CabinetPath = (typeof CABINET_PATHS)[keyof typeof CABINET_PATHS]

export type NavSectionId = keyof typeof CABINET_PATHS

export interface NavSection {
  readonly id: NavSectionId
  /**
   * Подпись по-русски.
   *
   * ОСТАЁТСЯ РЯДОМ С КЛЮЧОМ ПЕРЕВОДА, а не заменяется им: её читают места, где React'а
   * нет и словарь взять негде, — сборка маршрутов, заголовки страниц, отказ раздела
   * в тестах. Расхождение при этом невозможно: сторож сверяет её с русским словарём.
   */
  readonly label: string
  /**
   * Ключ перевода подписи. Тип закрытый, поэтому забытый раздел — ошибка сборки,
   * а не «undefined» в боковом меню у человека, который читает интерфейс на неродном
   * языке и без того не уверен, туда ли он нажал.
   */
  readonly textKey: TextKey
  /**
   * Ключ ДВУХБУКВЕННОГО сокращения для иконочной навигации 56 px.
   *
   * Отдельным ключом, а не срезом от названия: по-английски Services и Settings дают
   * одно и то же «Se», и различить их в узком столбце нечем — подсказки у планшета нет.
   * Сокращение — решение переводчика, как и само название.
   */
  readonly shortKey: TextKey
  /**
   * Та же подпись по-немецки. В интерфейс она НЕ попадает: этап 0 сдаётся по-русски.
   * Она здесь ровно для одной работы — проверки «текст +40 %» из макета: немецкий даёт
   * настоящий прирост длины («Тариф и оплата» → «Tarif und Zahlung», «Каталог» →
   * «Leistungskatalog»), а не выдуманный lorem, на котором вёрстка всегда выглядит целой.
   * Строки взяты из макета дословно, чтобы проверка совпадала с тем, что видел дизайнер.
   */
  readonly labelLong: string
  readonly path: CabinetPath
  /**
   * Право, без которого раздел не показывается. null — раздел доступен любому вошедшему:
   * поддержка нужна в том числе тому, у кого не хватает прав на всё остальное.
   */
  readonly permission: KnownPermission | null
  /**
   * Осмыслен ли в разделе режим «Все филиалы» (ТЗ §4.4).
   *
   * У журнала его НЕТ и быть не может: день двух филиалов в разных часовых поясах
   * в одной сетке нечитаем — в 10:00 одной колонки и 10:00 соседней разное абсолютное
   * время. Везде остальном «все точки» осмысленны, поэтому запрет один и он назван.
   */
  readonly allowsAllBranches: boolean
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: "summary",
    textKey: "nav.summary",
    shortKey: "navShort.summary",
    label: "Сводка",
    labelLong: "Tagesubersicht",
    path: CABINET_PATHS.summary,
    // reports.view, а не schedule.view: сводку открывают бухгалтер и наблюдатель,
    // которым журнал закрыт намеренно — там имена клиентов.
    permission: "reports.view",
    allowsAllBranches: true,
  },
  {
    /*
     * ЦЕНТР УВЕДОМЛЕНИЙ СТОИТ ВТОРЫМ, ПЕРЕД ЖУРНАЛОМ, и это решение о работе стойки:
     * утро администратора начинается не с сетки дня, а с разбора того, что случилось
     * без него — онлайн-записи, ждущие подтверждения, и записи без мастера.
     *
     * ПРАВА НЕТ НИ ОДНОГО (null). Лента показывает РОВНО то, что адресовано этому
     * человеку, и решает это сервер построчно. Право на раздел означало бы «кто видит
     * журнал, видит всю ленту» — то есть открыло бы мастеру события чужих записей,
     * которых он в журнале как раз не видит. Пункт виден всем, у кого есть кабинет,
     * и у мастера он тоже осмыслен: там его отмены, переносы и правки графика.
     */
    id: "notifications",
    textKey: "nav.notifications",
    shortKey: "navShort.notifications",
    label: "Уведомления",
    labelLong: "Benachrichtigungen",
    path: CABINET_PATHS.notifications,
    permission: null,
    // Лента событий сети целиком осмысленна: филиал — это фильтр, а не разные ленты.
    allowsAllBranches: true,
  },
  {
    id: "journal",
    textKey: "nav.journal",
    shortKey: "navShort.journal",
    label: "Журнал",
    labelLong: "Terminkalender",
    path: CABINET_PATHS.journal,
    permission: "schedule.view",
    allowsAllBranches: false,
  },
  {
    id: "clients",
    textKey: "nav.clients",
    shortKey: "navShort.clients",
    label: "Клиенты",
    labelLong: "Kundenstamm",
    path: CABINET_PATHS.clients,
    permission: "clients.list.view",
    allowsAllBranches: true,
  },
  {
    id: "catalog",
    textKey: "nav.catalog",
    shortKey: "navShort.catalog",
    label: "Каталог",
    labelLong: "Leistungskatalog",
    path: CABINET_PATHS.catalog,
    permission: "catalog.view",
    allowsAllBranches: true,
  },
  {
    id: "staff",
    textKey: "nav.staff",
    shortKey: "navShort.staff",
    label: "Сотрудники",
    labelLong: "Mitarbeitende",
    path: CABINET_PATHS.staff,
    permission: "staff.view",
    allowsAllBranches: true,
  },
  {
    id: "branches",
    textKey: "nav.branches",
    shortKey: "navShort.branches",
    label: "Филиалы",
    labelLong: "Standorte",
    path: CABINET_PATHS.branches,
    permission: "branches.view",
    allowsAllBranches: true,
  },
  {
    id: "reports",
    textKey: "nav.reports",
    shortKey: "navShort.reports",
    label: "Отчёты",
    labelLong: "Auswertungen",
    path: CABINET_PATHS.reports,
    permission: "reports.view",
    allowsAllBranches: true,
  },
  {
    id: "channels",
    textKey: "nav.channels",
    shortKey: "navShort.channels",
    label: "Каналы",
    labelLong: "Kanäle",
    path: CABINET_PATHS.channels,
    permission: "channels.manage",
    allowsAllBranches: true,
  },
  {
    id: "campaigns",
    textKey: "nav.campaigns",
    shortKey: "navShort.campaigns",
    label: "Рассылки и сегменты",
    labelLong: "Mailings und Segmente",
    path: CABINET_PATHS.campaigns,
    // Рассылки шлёт тот же, кто настраивает каналы: отдельного права под них пока нет,
    // а выдумывать код, которого нет в матрице ролей, нельзя — он никому не выдастся.
    permission: "channels.manage",
    allowsAllBranches: true,
  },
  {
    id: "audit",
    textKey: "nav.audit",
    shortKey: "navShort.audit",
    label: "Журнал действий",
    labelLong: "Aktionsprotokoll",
    path: CABINET_PATHS.audit,
    permission: "audit.view",
    allowsAllBranches: true,
  },
  {
    id: "billing",
    textKey: "nav.billing",
    shortKey: "navShort.billing",
    label: "Тариф и оплата",
    labelLong: "Tarif und Zahlung",
    path: CABINET_PATHS.billing,
    permission: "billing.manage",
    allowsAllBranches: true,
  },
  {
    id: "developers",
    textKey: "nav.developers",
    shortKey: "navShort.developers",
    label: "Разработчикам",
    labelLong: "Für Entwickler",
    path: CABINET_PATHS.developers,
    permission: "api_keys.manage",
    allowsAllBranches: true,
  },
  {
    id: "settings",
    textKey: "nav.settings",
    shortKey: "navShort.settings",
    label: "Настройки",
    labelLong: "Einstellungen",
    path: CABINET_PATHS.settings,
    permission: "settings.view",
    allowsAllBranches: true,
  },
  {
    id: "support",
    textKey: "nav.support",
    shortKey: "navShort.support",
    label: "Поддержка",
    labelLong: "Kundendienst",
    path: CABINET_PATHS.support,
    permission: null,
    allowsAllBranches: true,
  },
]

/** Подпись раздела для заголовка экрана и вкладки браузера. */
export function sectionLabel(path: string): string {
  return sectionByPath(path)?.label ?? "Кабинет"
}

/** Раздел, которому принадлежит адрес. Нужен шапке, переключателю филиалов и вкладкам. */
export function sectionByPath(path: string): NavSection | null {
  return NAV_SECTIONS.find((section) => path.startsWith(section.path)) ?? null
}

/**
 * Сокращения, которые по правилу не считаются.
 *
 * Правило по умолчанию — первые две буквы названия, и у него был изъян: «Каталог»
 * и «Каналы» давали одинаковое «Ка». Отличить их можно было только подсказкой, то есть
 * наведением мыши, а на планшете подсказки нет вовсе. Дизайнер развёл пару первыми
 * буквами СЛОВ, и таблица переопределений теперь живёт в токенах
 * (tokens.json → nav.iconLabelOverrides).
 *
 * Немецкие сокращения дизайнер назвал в ответе словами, в tokens.json их нет — поэтому
 * они заведены здесь, в одной таблице с русскими и рядом с теми же немецкими подписями,
 * что стоят в labelLong выше. Одна таблица, а не таблица на язык: сокращение обязано
 * переезжать вместе с названием, иначе при переводе кабинета они разойдутся.
 */
/**
 * Немецкие сокращения из ответа дизайнера.
 *
 * Остаются ЗДЕСЬ и только для проверки ширины: немецкий в переключателе не предлагается
 * (словаря у него нет), а labelLong существует ровно затем, чтобы верстка выдержала
 * язык на полтора раза длиннее русского. Когда появится немецкий словарь, сокращения
 * переедут в него, как русские и английские.
 */
export const GERMAN_ICON_LABELS: Readonly<Record<string, string>> = {
  Leistungskatalog: "Lk",
  Kanäle: "Kn",
}

/**
 * Две буквы для иконочной навигации 56 px — ИЗ СЛОВАРЯ, а не из подписи.
 *
 * Здесь было правило «первые две буквы» с таблицей исключений по русскому тексту.
 * Оно работало до первого перевода: по-английски Services и Settings дают одно и то же
 * «Se», а исключения для них взять было негде — таблица ключуется текстом, и англоязычных
 * ключей в ней нет. Теперь сокращение приходит тем же путём, что и название, то есть
 * ОБЯЗАНО существовать на каждом языке (тип Dictionary) и не совпадать внутри языка
 * (сторож nav-items.test.ts).
 */
export function shortTextKey(section: NavSection): TextKey {
  return section.shortKey
}

const BY_ID = new Map<NavSectionId, NavSection>(NAV_SECTIONS.map((section) => [section.id, section]))

/**
 * Раздел по идентификатору. Нужен маршрутам: право раздела описано ОДИН раз, здесь,
 * иначе навигация и маршрут однажды разойдутся — пункт в меню есть, а экран отказывает.
 */
export function sectionById(id: NavSectionId): NavSection {
  const found = BY_ID.get(id)
  if (found === undefined) throw new Error(`раздел ${id} не описан в NAV_SECTIONS`)
  return found
}
